//
// # Metal Layer and Via Definitions
//

import { Direction } from "./core";

export interface Color {
  /**
   * Red component (0-255)
   * @type int
   */
  r: number;
  /**
   * Green component (0-255)
   * @type int
   */
  g: number;
  /**
   * Blue component (0-255)
   * @type int
   */
  b: number;
  /**
   * Alpha component (0-255)
   * @type int
   */
  a?: number;
  name?: string;
}

// ============================================================================
// Metal Layer Definition (v6 - Simplified)
// ============================================================================
//
// In v6, MetalLayer no longer duplicates fields from PDKLayer.
// - Removed: name, color (lookup via layer ID in PDKLayer)
// - Changed: layer field now references GenAlpha layer ID (not OA number)
// - Changed: purpose field now required (references GenAlpha purpose ID)
//

export interface MetalLayer {
  /**
   * GenAlpha layer ID (references PDKLayer.number)
   * @type int
   */
  layer: number;
  /**
   * GenAlpha purpose ID (references PDKLayerPurpose.number)
   * @type int
   */
  purpose: number;

  direction: Direction;

  /**
   * The design-rule manual's metal type for this level in this stack
   * (`x`, `y`, `z`…), where the manual has one.
   */
  metalType?: string;

  /**
   * Metal pitch in nanometers (nm)
   * @type int
   */
  pitch: number;
  /**
   * Metal width in nanometers (nm)
   * @type int
   */
  width: number;
  /**
   * Minimum legal route width on this layer in nanometers (nm).
   * Bounds user-specified per-route widths. Defaults to `width` when absent.
   * @type int
   */
  minWidth?: number;
  /**
   * Maximum legal route width on this layer in nanometers (nm).
   * Bounds user-specified per-route widths. When absent, the editor falls back
   * to a permissive default (see widthConstraints in GenAlphaModel).
   * @type int
   */
  maxWidth?: number;
  /**
   * Minimum edge-to-edge spacing between different-net wires on this layer in
   * nanometers (nm). Routers keep new wires at least this far from other nets'
   * metal on the same layer (same-net wires may still abut). When absent, no
   * spacing is enforced (0 — the historical behavior).
   * @type int
   */
  minSpacing?: number;

  // Electrical parameters for parasitic extraction (all optional for backward compatibility)

  /**
   * Sheet resistance in Ohms per square (Ω/□)
   * Used for wire resistance calculation: R = Rsheet * L / W
   */
  sheetResistanceOhmsPerSq?: number;

  /**
   * Metal thickness in nanometers (nm)
   * Used for capacitance calculations
   * @type int
   */
  thicknessNm?: number;

  /**
   * Capacitance to substrate/adjacent layers in femtofarads per micrometer (fF/µm)
   * Simplified capacitance model for routing cost estimation
   */
  capacitanceFfPerUm?: number;

  /**
   * Lateral coupling capacitance in femtofarads per micrometer (fF/µm)
   *
   * The coefficient of the closed-form coupling model, not a value that holds
   * "at minimum spacing": extraction scales it by a spacing-dependent decay,
   * so it is only the raw fF/µm at zero separation. Used as the fallback when
   * the layer carries no couplingTable, and as the A in that table's
   * fallbackExponent form.
   *
   * If not specified, coupling capacitance extraction is skipped for this layer.
   */
  couplingCapFfPerUm?: number;

  /**
   * Cutoff distance multiplier for coupling capacitance calculations.
   * Coupling is ignored for wires spaced more than (couplingCutoffMultiplier * minSpacing) apart.
   * If not specified, defaults to 8.
   */
  couplingCutoffMultiplier?: number;

  /**
   * Measured coupling capacitance on a (spacing, width) grid, in fF/µm.
   *
   * Present on layers characterised against a sign-off extractor; absent
   * everywhere else, in which case extraction falls back to
   * couplingCapFfPerUm and a closed-form decay. See CouplingTable.
   */
  couplingTable?: CouplingTable;

  /**
   * Ground capacitance a single neighbour shields, in fF/um of parallel run,
   * on the same (spacing, width) grid as couplingTable.
   *
   * A wire near other metal loses substrate field to it, so its ground
   * capacitance falls. Measured on StarRC across both PDKs, that loss is
   * additive and exactly linear in neighbour count: backing an isolated-wire
   * ground out of the sweep as (2*aggressor - victim) gives a value constant to
   * within 0.01% over a 6x spacing range on all eight layers characterised.
   * That is what selects an additive per-neighbour term over a multiplicative
   * one.
   *
   * It is NOT field conservation. Per neighbour, the ground lost is only 28-48%
   * of the coupling gained, so the two do not trade off one for one.
   *
   * capacitanceFfPerUm is therefore the isolated-wire ground. Absent means no
   * shielding is applied, which is the historical behaviour: before #3034 our
   * ground was a per-layer constant, identical on a wire with two close
   * neighbours and one with none.
   */
  groundShieldingTable?: CouplingTable;

  /**
   * How the isolated-wire ground scales with conductor width.
   *
   * Absent means capacitanceFfPerUm is used at every width, which is the
   * historical behaviour: our ground did not move at all over a 4x width range
   * while StarRC's did. See GroundWidthCurve.
   */
  groundWidthCurve?: GroundWidthCurve;

  /**
   * Minimum polygon area, from the PDK's design-rule manual. A polygon's
   * minimum is the largest `area` among the cases whose conditions it meets.
   * Absent means not filled from the manual, never zero.
   */
  minArea?: AreaRule[];
  /** Minimum area of a hole the layer encloses, as cases like `minArea`. */
  minEnclosedArea?: AreaRule[];
  /**
   * Spacing that grows with width and parallel run length, one row per rule;
   * a pair of wires needs the largest `space` among the rows they meet.
   */
  spacingTable?: SpacingRow[];
  /** Line-end (end-of-line) spacing rules. */
  eol?: EolRule[];
  /**
   * The number of masks (colours) the layer is split across, when the manual
   * states it is multi-patterned.
   * @type int
   */
  masks?: number;
  /**
   * Minimum spacing between shapes on the same mask, in nm.
   * @type int
   */
  sameMaskSpacing?: number;
  /**
   * The only widths a wire may have, when the manual lists them; one entry per
   * orientation the manual distinguishes.
   */
  allowedWidths?: AllowedWidths[];
  /** Metal density limits, one per rule. */
  density?: DensityRule[];
  /**
   * Where each design-rule value came from, keyed by its field path in this
   * object (`"minArea[1]"`, `"minWidth"`).
   */
  drmSource?: Record<string, DrmCite>;
}

/** One minimum-area case, and the polygons it applies to. With no condition it applies to every polygon on the layer. */
export interface AreaRule {
  /**
   * Square nanometers (nm²).
   * @type int
   */
  area: number;
  /** Applies only to rectangles, or only to other polygons. */
  shape?: "rectangle" | "nonRectangle";
  /**
   * Applies when every edge is shorter than this, in nm.
   * @type int
   */
  allEdgesLt?: number;
  /**
   * Applies when at least one edge is at least this long, in nm.
   * @type int
   */
  anyEdgeGe?: number;
  /**
   * Applies when the polygon is at least this wide, in nm.
   * @type int
   */
  widthGe?: number;
  /** The manual recommends this area; it is not a minimum. */
  recommended?: boolean;
}

/**
 * One row of a width / run-length spacing table, in nm. A row applies when the
 * measured wire's width, the other wire's width and the pair's parallel run
 * length are within their bounds (an absent bound is no condition).
 */
export interface SpacingRow {
  /** @type int */
  space: number;
  width?: Bounds;
  otherWidth?: Bounds;
  /** Parallel run length; negative when the wires overlap by less than nothing. */
  runLength?: Bounds;
  /** Only between shapes on the same mask. */
  sameMask?: boolean;
  /**
   * Layers the rule is not checked over. `"self"`: skipped only where the gap
   * is this layer's own metal, so always checked between separate shapes
   * (inferred from the manual's definition of the clause and its figure,
   * cited in the PDK's `drm/stack-data.md`). Any other name is a
   * layer or marker the stack does not model; check strictly.
   */
  notCheckedOver?: string[];
  /** The manual recommends this; it is not a minimum. */
  recommended?: boolean;
}

/**
 * Bounds on a length in nm, each as the manual writes it (`>`, `>=`, `<`,
 * `<=`, `=`). A value is within them when it meets every one given.
 */
export interface Bounds {
  /** @type int */
  gt?: number;
  /** @type int */
  ge?: number;
  /** @type int */
  lt?: number;
  /** @type int */
  le?: number;
  /** @type int */
  eq?: number;
}

/** A line-end spacing rule, in nm: from a line end to what `to` names. */
export interface EolRule {
  /** @type int */
  space: number;
  /** Another line end, the side of another wire, or any metal on the layer. */
  to: "lineEnd" | "side" | "any";
  /** The width of a line end: the bounds an edge is within to be one. */
  endWidth?: Bounds;
  /** Whether `endWidth` holds for both line ends or for one of them. */
  ends?: "both" | "one";
  /**
   * The line end is extended this far on each side before it is measured.
   * @type int
   */
  extend?: number;
  runLength?: Bounds;
  sameMask?: boolean;
  recommended?: boolean;
  /**
   * The rule this is one alternative of: entries naming the same rule are
   * options, and meeting any one of them meets it.
   */
  alternativeOf?: string;
}

/** Discrete legal widths, in nm, and optionally any width from `atLeast` up. */
export interface AllowedWidths {
  /** @type int */
  values: number[];
  /** @type int */
  atLeast?: number;
  /** Wires in the layer's preferred or non-preferred direction; absent for both. */
  orientation?: "preferred" | "nonPreferred";
}

/** A density limit, in percent of a window. One of `min` / `max` is given. */
export interface DensityRule {
  min?: number;
  max?: number;
  /**
   * The square checking window's side, in nm; absent for the whole chip.
   * @type int
   */
  window?: number;
  /**
   * How far the window steps, in nm.
   * @type int
   */
  step?: number;
  recommended?: boolean;
}

/** The rule, page and table of the design-rule manual a value came from. */
export interface DrmCite {
  /** The rule ID, verbatim; absent for a value read from a table. */
  rule?: string;
  /** @type int */
  page: number;
  table?: string;
  /** A typed rule's value, a table cell that is not a rule row, computed from cited values, or a sentence of the manual. */
  how: "rule" | "table" | "derived" | "text";
}

/**
 * Isolated-wire ground capacitance against conductor width, in fF/um.
 *
 * A one-dimensional curve rather than a CouplingTable, because that is what the
 * measurement says it is. Backing the isolated ground out of the GF22 sweep as
 * (2*aggressor - victim) at every one of the 30 (spacing, width) cells gives a
 * value that varies with width - +18.2% to +40.9% over a 4x width range - and
 * is constant down every spacing column to five decimal places. Width and
 * spacing separate completely here, unlike the coupling term, where they do not.
 *
 * The shielding term keeps its own two-dimensional table: a neighbour's effect
 * depends on both how far away it is and how wide the pair are.
 *
 * Mirrors `GroundWidthCurve` in crates/schema/src/current/metal.rs.
 */
export interface GroundWidthCurve {
  /**
   * Conductor width as a multiple of the layer's nominal width. Ascending,
   * at least two entries.
   */
  widthMultiples: number[];

  /**
   * Isolated-wire ground in fF/um at each width. Same length as widthMultiples.
   *
   * Outside the measured range the nearest end is held rather than
   * extrapolated: ground is bounded by geometry, so a flat hold is the
   * conservative reading.
   */
  ffPerUm: number[];
}

/**
 * Measured coupling capacitance for one metal layer, on a (spacing, width) grid.
 *
 * Indexed on *multiples* rather than absolute nanometers, so one table shape
 * serves every layer and minimum spacing keeps its single definition
 * (pitch - width) rather than being restated per layer.
 *
 * No single closed form fits every layer. Across the GF12 and GF22 sweeps
 * (#3033) six of eight layers favour A*s^(-b) with b in 0.63-0.84, while
 * gf12 K1 and gf22 M1 resist all three candidate families and give the worst
 * residuals. A table represents what a closed form cannot, so characterised
 * layers carry one.
 *
 * Fit quality and model error are separate properties, and they do not pick
 * the same layers: gf22 IA has the *best* fit in the set (under 1% rms) and yet
 * one of the largest remaining errors against StarRC, because it is the
 * thickest layer and our model reads no thickness term at all.
 *
 * Mirrors `CouplingTable` in crates/schema/src/current/metal.rs.
 */
export interface CouplingTable {
  /**
   * Row index: edge-to-edge spacing, as a multiple of the layer's minimum
   * spacing. Ascending, at least two entries.
   */
  spacingMultiples: number[];

  /**
   * Column index: conductor width, as a multiple of the layer's nominal
   * width. Ascending, at least two entries.
   */
  widthMultiples: number[];

  /**
   * Coupling capacitance in fF/µm of parallel run, per neighbour.
   *
   * ffPerUm[i][j] is the value at spacingMultiples[i] and widthMultiples[j],
   * so the outer length matches spacingMultiples and every inner length
   * matches widthMultiples.
   */
  ffPerUm: number[][];

  /**
   * Exponent b of the A*s^(-b) fallback used outside the table's range, with A
   * taken from couplingCapFfPerUm.
   *
   * Absent means extrapolation is refused rather than guessed: on the two
   * layers whose fits are poor, an honest gap beats an extrapolation already
   * known to be wrong.
   */
  fallbackExponent?: number;
}

// ============================================================================
// Via Definition (v6 - Updated)
// ============================================================================
//
// In v6, ViaDefinition uses layer IDs instead of string names for consistency
// - Changed: lowerLayer, upperLayer now use GenAlpha layer IDs
// - Removed: layerName (redundant, lookup via layer ID)
// - Changed: layer now references GenAlpha layer ID (not OA number)
//

/**
 * Format-specific identification for a via definition.
 *
 * Deliberately NOT the shared `Exports` type that layers and purposes use.
 * A layer is identified in OpenAccess by a NUMBER, so `Exports.oa` is numeric;
 * an `oaViaDef` is identified by a NAME. GenAlpha's stack calls the M1-M2 via
 * `V1`, while the GF12 technology calls the same via `M1V1M2` — a string, with
 * no numeric form to fall back on.
 *
 * Reusing `Exports` would have meant adding a via-only string field to a type
 * three other things share, so the difference is expressed in the type instead.
 */
export interface ViaExports {
  /**
   * `oaViaDef` name in the target technology, e.g. "M1V1M2".
   *
   * Without it, an export writes GenAlpha's own via name and the NoaCpp writer
   * rejects it against a real tech DB ("Via def not found in tech DB: V1") —
   * a loud failure naming its own fix. See `NoaLib/src/conv/routes.ts`.
   */
  oa?: string;
}

export interface ViaDefinition {
  name: string;

  /**
   * How this via is identified in export formats. Optional: a stack that has
   * never been mapped to a customer technology has nothing truthful to put
   * here, and an absent entry fails loudly at write time rather than silently
   * writing a wrong name.
   */
  exports?: ViaExports;
  /**
   * GenAlpha layer ID for lower metal (references PDKLayer.number)
   * @type int
   */
  lowerLayer: number;
  /**
   * GenAlpha layer ID for upper metal (references PDKLayer.number)
   * @type int
   */
  upperLayer: number;

  /**
   * GenAlpha layer ID for via layer (references PDKLayer.number)
   * @type int
   */
  layer: number;
  /**
   * Via width in nanometers (nm)
   * @type int
   */
  width: number;
  /**
   * Via height in nanometers (nm)
   * @type int
   */
  height: number;

  spacing: {
    /**
     * Horizontal spacing in nanometers (nm)
     * @type int
     */
    horizontal: number;
    /**
     * Vertical spacing in nanometers (nm)
     * @type int
     */
    vertical: number;
  };

  enclosure: {
    lower: {
      /**
       * Horizontal enclosure in nanometers (nm)
       * @type int
       */
      horizontal: number;
      /**
       * Vertical enclosure in nanometers (nm)
       * @type int
       */
      vertical: number;
      /**
       * Offset [x, y] in nanometers (nm)
       * @type int
       */
      offset?: [number, number];
    };
    upper: {
      /**
       * Horizontal enclosure in nanometers (nm)
       * @type int
       */
      horizontal: number;
      /**
       * Vertical enclosure in nanometers (nm)
       * @type int
       */
      vertical: number;
      /**
       * Offset [x, y] in nanometers (nm)
       * @type int
       */
      offset?: [number, number];
    };
  };

  // Electrical parameters for parasitic extraction (optional for backward compatibility)

  /**
   * Resistance per via in Ohms (Ω)
   * Total via resistance = resistanceOhms / numberOfVias (for parallel vias)
   */
  resistanceOhms?: number;

  /**
   * Vertical extent of the via cut in nanometers (nm) - the inter-metal
   * dielectric thickness between lowerLayer and upperLayer.
   *
   * Not to be confused with `height`, which is planar: width == height on 201
   * of the 203 vias across every shipped stack, i.e. they are square cut
   * footprints. Nothing else in the schema carries vertical separation -
   * MetalLayer.thicknessNm is a conductor's own z-extent, Dielectric carries k
   * but no thickness, and Stack has no z information at all.
   *
   * A via is the natural home for it: it already declares which two metals it
   * joins, so there is no z-ordering to infer and no stack-height table to keep
   * consistent.
   *
   * Do NOT build an area-based vertical model on this. Backing the gap out of
   * our own plate measurement by parallel plate (d = k*eps0*A/C, C = 0.9307 fF
   * on gf12_plate_M1_M2_w32_p4000) gives 7.3nm against a real M1-M2 gap of
   * 60-90nm - off by about 12x. At these geometries a 32nm wire under a 4um
   * plate is fringe-dominated, not area-dominated. The measured per-crossing
   * constant and the saturating plate curve are the basis; this field indexes
   * them.
   *
   * Mirrors `vertical_extent_nm` in crates/schema/src/current/metal.rs.
   */
  verticalExtentNm?: number;

  /**
   * Capacitance in femtofarads (fF) of one orthogonal crossing between
   * lowerLayer and upperLayer, at both layers' nominal widths.
   *
   * Measured, not derived. #3033 found crossings linear in count - ~0.013 fF
   * each on the Mx layers, constant to within 5% over k = 1..8 - so a
   * per-crossing constant captures almost all of the inter-layer term. It
   * saturates only on the coarsest layer measured (gf22 IA, -34% by k = 8),
   * which is a documented limit rather than a mechanism.
   *
   * Width scaling is deliberately absent: the plate sweep saturates by about
   * 1um, where 8x more plate metal buys 1.5% more capacitance.
   *
   * Absent means inter-layer coupling is not extracted for this pair, which is
   * the historical behaviour - extraction returned 0.0 on any layer mismatch,
   * and #3033 measured all 63 inter-layer structures at exactly 0.0000 fF on
   * our side against up to 1.42 fF (89.3% of a net) in StarRC.
   *
   * Mirrors `crossing_cap_ff` in crates/schema/src/current/metal.rs.
   */
  crossingCapFf?: number;

  /**
   * The minimum enclosure by each metal, from the PDK's design-rule manual, as
   * options: meeting any one option of a metal meets the rules for that metal.
   * Absent means not filled from the manual, never zero.
   */
  enclosureAlternatives?: ViaEnclosureOptions;
  /** The enclosure the manual recommends, as options; not a minimum. */
  recommendedEnclosure?: ViaEnclosureOptions;
  /** The cut sizes the manual allows, `[width, height]` in nm. */
  sizes?: [number, number][];
  /** Minimum spacing to the cuts of other via layers. */
  viaSpacing?: ViaToVia[];
  /** Where each design-rule value came from, keyed by its field path in this object. */
  drmSource?: Record<string, DrmCite>;
}

/** One enclosure option, in nm: on the horizontal and on the vertical sides. */
export interface ViaEnclosureOption {
  /** @type int */
  horizontal: number;
  /** @type int */
  vertical: number;
  /** @type int */
  offset?: [number, number];
}

/** Enclosure options for each metal of a via. */
export interface ViaEnclosureOptions {
  lower?: ViaEnclosureOption[];
  upper?: ViaEnclosureOption[];
}

/** Minimum spacing from a via's cuts to another via layer's, in nm. */
export interface ViaToVia {
  /** The other via layer's name. */
  via: string;
  /** @type int */
  spacing: number;
  /** Measured centre to centre rather than edge to edge. */
  centerToCenter?: boolean;
  runLength?: Bounds;
  /** Vias on the same net are exempt. */
  sameNetExempt?: boolean;
}
